import { zodResolver } from '@hookform/resolvers/zod';
import useCollectionCreate from 'hooks/useCollectionCreate';
import { useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  CollectionModalForm,
  collectionModalFormSchema,
  initialCollectionModalFormValues,
} from './collectionEditModal';
import { CollectionModalBody } from './components/collectionModalBody';

type CollectionAddModalProps = Record<string, never>;

export function CollectionAddModal(_: CollectionAddModalProps): JSX.Element {
  const { hideModal } = useModal();
  const { successNotify, errorNotify } = useNotification();
  const [createMutation, mutationResult] = useCollectionCreate();

  const {
    control,
    formState: { isValid, isDirty },
    handleSubmit,
    watch,
  } = useForm<CollectionModalForm>({
    resolver: zodResolver(collectionModalFormSchema),
    defaultValues: initialCollectionModalFormValues,
    reValidateMode: 'onChange',
    mode: 'all',
  });

  const navigate = useNavigate();

  const collectionTitle = watch('collectionTitle');
  const createCollection = useCallback(
    /** Handles collection creation as well as notification for success/failure */
    function createCollectionAndNotify() {
      return createMutation({
        title: collectionTitle,
        internalKbEnabled: watch('internalKbEnabled'),
      })
        .then((res) => {
          if (res.data?.collectionCreate) {
            hideModal();
            successNotify({
              title: 'Collection Created',
              text: `"${collectionTitle}" has been created.`,
            });
            navigate(`/knowledge/collection/${res.data.collectionCreate.id}`);
          }
        })
        .catch(() => {
          errorNotify({
            title: 'Unable to create your collection',
            text: `Please try again. If this issue persists, please contact support.`,
            time: 5000,
          });
        });
    },
    [
      createMutation,
      collectionTitle,
      watch,
      hideModal,
      successNotify,
      navigate,
      errorNotify,
    ],
  );

  return (
    <CollectionModalBody
      title="New collection"
      control={control}
      disabled={mutationResult.loading}
      loading={mutationResult.loading}
      isValid={isValid && isDirty}
      closeModal={hideModal}
      onSave={handleSubmit(createCollection)}
    />
  );
}
